import { useCallback, useState } from 'react'

import { PaginationProps, useErrorSnackbar } from 'shared/hooks'
import { AddActionHandler, AddActionOption } from '../types'

export interface SingleSelectDialogController {
  query: string
  setQuery: (value: string) => void
  dataLoading: boolean
  actionLoading: boolean
  options: AddActionOption[]
  pagination: PaginationProps
  itemToAdd: AddActionOption | null
  requestAdd: (item: AddActionOption) => void
  cancelAdd: () => void
  confirmAdd: () => Promise<void>
}

interface UseSingleSelectDialogControllerArgs {
  query: string
  setQuery: (value: string) => void
  dataLoading: boolean
  options: AddActionOption[]
  pagination: PaginationProps
  onSubmit: AddActionHandler
  onSuccess?: () => Promise<unknown> | void
}

export const useSingleSelectDialogController = ({
  query,
  setQuery,
  dataLoading,
  options,
  pagination,
  onSubmit,
  onSuccess,
}: UseSingleSelectDialogControllerArgs): SingleSelectDialogController => {
  const [itemToAdd, setItemToAdd] = useState<AddActionOption | null>(null)

  const [actionLoading, setActionLoading] = useState(false)
  const [showError] = useErrorSnackbar()

  const requestAdd = useCallback((item: AddActionOption) => {
    if (item.disabled) return

    setItemToAdd(item)
  }, [])

  const cancelAdd = useCallback(() => {
    if (actionLoading) return

    setItemToAdd(null)
  }, [actionLoading])

  const confirmAdd = useCallback(async () => {
    if (!itemToAdd || actionLoading) return

    setActionLoading(true)

    try {
      await onSubmit(itemToAdd.value)
      await onSuccess?.()

      setItemToAdd(null)
    } catch {
      showError()
    } finally {
      setActionLoading(false)
    }
  }, [actionLoading, onSubmit, onSuccess, showError, itemToAdd])

  return {
    query,
    setQuery,
    dataLoading,
    actionLoading,
    options,
    pagination,
    itemToAdd,
    requestAdd,
    cancelAdd,
    confirmAdd,
  }
}
